import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import useConsultantPWAManifest from "@/lib/useConsultantPWAManifest";
import ConsultantHeader from "@/components/ConsultantHeader";
import { WordMark } from "@/components/Logo";
import { Loader2, CheckCircle2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function ClientSign() {
  const { practiceId, slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = params.get("code");
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resending, setResending] = useState(false);
  const [consultant, setConsultant] = useState(null);

  useConsultantPWAManifest(slug, consultant);

  useEffect(() => {
    if (slug) {
      api.get(`/consultants/by-slug/${slug}`).then(({ data }) => setConsultant(data)).catch(() => {});
    }
  }, [slug]);

  const verify = async () => {
    if (otp.length !== 6) return;
    setVerifying(true);
    try {
      await api.post("/practices/verify-otp", { practice_id: practiceId, otp });
      setVerified(true);
      toast.success("Firma OTP confermata!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "OTP errato");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (otp.length === 6 && !verified) verify();
  }, [otp]);

  const resend = async () => {
    setResending(true);
    try {
      await api.post(`/practices/${practiceId}/resend-otp`);
      toast.success("Nuovo OTP inviato via email");
    } catch {
      toast.error("Errore reinvio OTP");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <ConsultantHeader consultant={consultant} backTo={slug ? `/c/${slug}` : null} />

      <main className="max-w-md mx-auto px-5 py-8">
        {/* Big success */}
        <div className="card-gs text-center mb-6 !pt-8">
          <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">Pratica creata</div>
          <div data-testid="practice-code-display" className="text-3xl sm:text-4xl font-black text-[#1E293B] mb-2 tracking-tight">{code}</div>
          <p className="text-sm text-slate-600">La tua segnalazione è stata inviata al consulente.</p>
        </div>

        {!verified ? (
          <div className="card-gs">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-5 h-5 text-[#2456C6]" />
              <h2 className="text-lg font-bold text-[#1E293B]">Firma con OTP</h2>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              Ti abbiamo inviato un codice a 6 cifre via email. Inseriscilo per firmare digitalmente la pratica (FES eIDAS).
            </p>
            <div className="flex justify-center mb-4">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                <InputOTPGroup>
                  {[0,1,2,3,4,5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="w-11 h-12 text-xl font-black border-2" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <button onClick={verify} disabled={verifying || otp.length !== 6} data-testid="verify-otp-button"
              className="btn-gs-primary w-full disabled:opacity-50">
              {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : "Conferma OTP"}
            </button>
            <button onClick={resend} disabled={resending} data-testid="resend-otp-button"
              className="w-full mt-3 text-sm text-slate-600 hover:text-[#2456C6] py-2 font-bold">
              {resending ? "Invio…" : "Non ho ricevuto il codice"}
            </button>
          </div>
        ) : (
          <div className="card-gs text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
              <Mail className="w-6 h-6 text-[#2456C6]" />
            </div>
            <h2 className="text-lg font-bold text-[#1E293B] mb-2">Ora conferma via email</h2>
            <p className="text-sm text-slate-600 mb-5">
              Abbiamo inviato un link al tuo indirizzo email. Cliccaci sopra per completare la firma elettronica. Puoi farlo anche più tardi.
            </p>
            <Link to={`/c/${slug}`} data-testid="back-to-home-button" className="btn-gs-secondary w-full">
              Torna alla home
            </Link>
          </div>
        )}

        <div className="text-center mt-8">
          <WordMark className="text-sm" />
          <div className="text-[10px] text-slate-400 mt-1">Marchio di TotalKlick srls</div>
        </div>
      </main>
    </div>
  );
}
